import { type IsoWeekday, MENU_MAX_DISHES_PER_DAY } from '@pulsify/shared/dto'
import { ChevronLeft, ChevronRight, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import * as m from '~/paraglide/messages'
import { cn } from '~/shared/lib/utils'
import { Button } from '~/shared/ui/button'
import { SegmentedControl } from '~/shared/ui/segmented-control'
import {
  addLunchDay,
  ISO_WEEKDAYS,
  type LunchMenuDraft,
  removeLunchDay,
  shiftLunchWeek,
  shownLunchDay,
  updateLunchDay,
} from '../lib/menu-editor-model'
import { formatMonday, weekdayLabel } from '../lib/menu-format'
import { DishRows } from './dish-rows'

interface LunchMenuEditorProps {
  draft: LunchMenuDraft
  locale: string
  onChange: (draft: LunchMenuDraft) => void
}

export function LunchMenuEditor({ draft, locale, onChange }: LunchMenuEditorProps) {
  const [picked, setPicked] = useState<IsoWeekday | null>(null)
  const shown = shownLunchDay(draft, picked)
  const shownDay = draft.days.find((day) => day.weekday === shown)

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-2">
        <SegmentedControl
          label={m.menuEditorWeekLabel()}
          value={draft.weekMode}
          options={[
            { value: 'week', label: m.menuWeekOf({ date: formatMonday(draft.weekOf, locale) }) },
            { value: 'every_week', label: m.menuEveryWeek() },
          ]}
          onChange={(weekMode) => onChange({ ...draft, weekMode })}
        />
        {draft.weekMode === 'week' && (
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={m.menuEditorPreviousWeek()}
              onClick={() => onChange(shiftLunchWeek(draft, -1))}
            >
              <ChevronLeft />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={m.menuEditorNextWeek()}
              onClick={() => onChange(shiftLunchWeek(draft, 1))}
            >
              <ChevronRight />
            </Button>
          </div>
        )}
      </div>

      <fieldset className="flex flex-wrap gap-1.5">
        <legend className="sr-only">{m.menuEditorDaysLabel()}</legend>
        {ISO_WEEKDAYS.map((weekday) => {
          const day = draft.days.find((entry) => entry.weekday === weekday)
          if (!day) {
            return (
              <button
                key={weekday}
                type="button"
                aria-label={m.menuEditorAddDay({ day: weekdayLabel(weekday, locale, 'long') })}
                onClick={() => {
                  onChange(addLunchDay(draft, weekday))
                  setPicked(weekday)
                }}
                className="flex min-w-12 flex-col items-center rounded-lg border border-dashed px-3 py-1.5 text-muted-foreground text-xs transition-colors duration-150 ease-spring hover:border-brand/40 hover:text-foreground"
              >
                <span>{weekdayLabel(weekday, locale)}</span>
                <Plus className="mt-0.5 size-3" aria-hidden />
              </button>
            )
          }
          const selected = weekday === shown
          return (
            <button
              key={weekday}
              type="button"
              aria-pressed={selected}
              onClick={() => setPicked(weekday)}
              className={cn(
                'flex min-w-12 flex-col items-center rounded-lg border px-3 py-1.5 font-medium text-xs transition-colors duration-150 ease-spring',
                selected
                  ? 'border-transparent bg-brand-subtle text-brand-subtle-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <span>{weekdayLabel(weekday, locale)}</span>
              <span className="mt-0.5 tabular-nums">{day.dishes.length}</span>
            </button>
          )
        })}
      </fieldset>

      {shownDay ? (
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-semibold font-serif text-foreground text-lg">
              {weekdayLabel(shownDay.weekday, locale, 'long')}
            </h3>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onChange(removeLunchDay(draft, shownDay.weekday))}
              className="text-muted-foreground hover:text-destructive"
            >
              <Trash2 />
              {m.menuEditorRemoveItem({ name: weekdayLabel(shownDay.weekday, locale, 'long') })}
            </Button>
          </div>
          <DishRows
            dishes={shownDay.dishes}
            limit={MENU_MAX_DISHES_PER_DAY}
            onChange={(dishes) => onChange(updateLunchDay(draft, shownDay.weekday, () => dishes))}
          />
        </div>
      ) : (
        <p className="border-t py-6 text-muted-foreground text-sm">{m.menuEditorNoDays()}</p>
      )}
    </div>
  )
}
